<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		Modal,
		Container,
		Button,
		PreviewBox,
		Tab,
		Tabs,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	import type { Device } from 'src/types/Device';
	import {
		DeviceUIField,
		type DeviceUIDeviceSidePanelView,
		type ViewCategory,
	} from 'src/types/DeviceUI';
	import { duiSaveField } from 'src/services/php-api/device_ui';
	import { getAvailableViews } from './view-helpers';
	import { deviceViewOptionsRegistry, viewComponentRegistry } from './view-registry';
	import { setDeviceViewStateContext } from './device-view-state-context';

	export let showModal = false;
	export let device: Device;
	export let category: ViewCategory | null = null;

	const dispatch = createEventDispatcher<{ save: { view: DeviceUIDeviceSidePanelView } }>();
	setDeviceViewStateContext({ showMandatoryUpdateAlert: false, ignoreOfflineState: true });

	$: options = category ? (deviceViewOptionsRegistry[category] ?? []) : getAvailableViews(device);
	$: basicView = options.find((opt) => opt.variant === 'basic');
	$: advancedView = options.find((opt) => opt.variant === 'advanced');

	$: componentRegistryKey =
		device?.type === 'peripheral-device' ? 'peripheral_device' : (basicView?.category ?? 'relay');
	$: basicComponent = viewComponentRegistry?.[componentRegistryKey]?.['basic'] ?? null;
	$: advancedComponent = viewComponentRegistry?.[componentRegistryKey]?.['advanced'] ?? null;

	const handleSave = async (view: DeviceUIDeviceSidePanelView) => {
		showModal = false;

		try {
			await duiSaveField(device.id, DeviceUIField.DEVICE_SIDE_PANEL_VIEW, view);
		} catch {
			toast.open({ type: 'danger', title: $format('error_global') });
		}

		dispatch('save', { view });
	};

	const handleClose = () => {
		showModal = false;
	};

	const handleSaveCurrentView = () => {
		if (activeTabIndex === 0 && basicView) {
			handleSave({
				category: basicView.category,
				variant: basicView.variant,
			});
		} else if (activeTabIndex === 1 && advancedView) {
			handleSave({
				category: advancedView.category,
				variant: advancedView.variant,
			});
		}
	};

	const tabItems = [
		{ text: $format('tab_basic_view', { default: 'Basic view' }) },
		{ text: $format('tab_advanced_view', { default: 'Advanced view' }) },
	];
	let activeTabIndex = 0;

	const handleOpen = () => {
		activeTabIndex = 0;
	};
</script>

<Modal
	on:open={handleOpen}
	on:close={handleClose}
	title={$format('modal_title_device_view_selection', {
		default: 'Choose how you want to view your device',
	})}
	{showModal}
	removeScrollPadding
>
	<Container slot="content" padding="scrollPaddings">
		<Text styleType="body-small" class="mb-l"
			>{$format('text_description_device_view_selection', {
				default: 'You can change your preferred view later from Settings',
			})}</Text
		>
		<Tabs class="mb-sm-m mb-md-l">
			{#each tabItems as item, i}
				<Tab
					active={i === activeTabIndex}
					on:click={() => {
						activeTabIndex = i;
					}}
				>
					{item.text}
				</Tab>
			{/each}
		</Tabs>

		<Text element="h2" class="mt-l mb-s">
			{#if activeTabIndex === 0}
				{$format('text_title_basic_view', {
					default: 'Basic – clean and minimal layout with essential controls',
				})}
			{:else if activeTabIndex === 1}
				{$format('text_title_advanced_view', {
					default: 'Advanced – shows detailed statistics and measurements',
				})}
			{/if}
		</Text>

		<PreviewBox backgroundImageSrc="./images/loginBackground.png" backgroundColor="color-base">
			<div class="preview-content">
				<Container padding="xs">
					<div class="d-flex flex-direction-column justify-content-center align-items-center">
						<Text
							element="p"
							styleType="body-small"
							class="text-center text-bold controls-large-styles text-nowrap"
							>{device.name}</Text
						>
						<Text
							element="p"
							styleType="body-small"
							class="text-center text-color-muted controls-small-styles text-nowrap mb-l"
							>Gen {device.gen}</Text
						>
					</div>
					<div class="mt-s" style="pointer-events: none; width: 300px; min-height: 80px;">
						{#if activeTabIndex === 0 && basicComponent}
							<svelte:component this={basicComponent} {device} />
						{:else if activeTabIndex === 1 && advancedComponent}
							<svelte:component this={advancedComponent} {device} />
						{/if}
					</div>
				</Container>
			</div>
		</PreviewBox>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={handleClose}
		>
			{$format('button_cancel')}
		</Button>
		<Button
			on:click={handleSaveCurrentView}
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{
				sm: 'm',
			}}
		>
			{$format('button_save')}
		</Button>
	</div>
</Modal>

<style>
	.preview-content {
		width: 316px;
		position: relative;
		z-index: 1;
	}
</style>
